import { FC, useEffect, useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { RecordingState } from './appointments/recording/RecordingState';
import { BufferedEventBus } from './utils/BufferedEventBus';
import { RecordingSuccessModal } from './appointments/recording/RecordingSuccessModal';
import { ProcessingCompleteModal } from './appointments/recording/ProcessingCompleteModal';
import { ShortRecordingModal } from './appointments/recording/ShortRecordingModal';
import { RecordingErrorModal } from './appointments/recording/RecordingErrorModal';
import { RecordingDirectUploadModal } from './appointments/recording/RecordingDirectUploadModal';
import { ISuccessMessage } from '@innerhive-internal/innerhive-common';
import { ViewNoteWithQueryParamDrawer } from './notes/view-note/ViewNoteWithQueryParamDrawer';
import {
    RECORDING_ERROR_EVENT,
    RECORDING_DIRECT_UPLOAD_START,
    RECORDING_DIRECT_UPLOAD_COMPLETE,
    RECORDING_DIRECT_UPLOAD_ERROR,
    RECORDING_DIRECT_UPLOAD_RETRY,
    RecordingService
} from './appointments/recording/RecordingService';

export const GlobalRecordingModals: FC = () => {
    const recordingState = useInstance(RecordingState);
    const recordingService = useInstance(RecordingService);
    const events = useInstance(BufferedEventBus);
    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);

    // Success modal state
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [successModalData, setSuccessModalData] = useState<{
        title: string;
        message: string;
        trackingType: 'auto-saved' | 'user-saved';
        trackingTitle: string;
        trackingMessage: string;
        trackingIsOffline: boolean;
    }>({
        title: '',
        message: '',
        trackingType: 'user-saved',
        trackingTitle: '',
        trackingMessage: '',
        trackingIsOffline: false
    });

    // Use ref to track current modal state to avoid closure issues in event handlers
    const successModalOpenRef = useRef(false);

    // Processing complete modal state
    const [processingCompleteModal, setProcessingCompleteModal] = useState<{
        isOpen: boolean;
        message: ISuccessMessage | null;
        noteId: string | null;
        trackingType: 'milestone' | 'encouragement';
        trackingTitle: string;
        trackingMessage: string;
        trackingRecordingCount: number;
        trackingMilestone: number | null;
        trackingTimePresent: number | null;
        trackingHandwritingSaved: number | null;
        trackingDuration: number | null;
    }>({
        isOpen: false,
        message: null,
        noteId: null,
        trackingType: 'encouragement',
        trackingTitle: '',
        trackingMessage: '',
        trackingRecordingCount: 0,
        trackingMilestone: null,
        trackingTimePresent: null,
        trackingHandwritingSaved: null,
        trackingDuration: null
    });

    // Short recording modal state
    const [shortRecordingModal, setShortRecordingModal] = useState<{
        isOpen: boolean;
        message: string;
        trackingWordCount: number;
        trackingDuration: number;
    }>({
        isOpen: false,
        message: '',
        trackingWordCount: 0,
        trackingDuration: 0
    });

    // Error modal state
    const [errorModal, setErrorModal] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        actionLabel: string;
        trackingErrorType: string;
    }>({
        isOpen: false,
        title: '',
        message: '',
        actionLabel: 'OK',
        trackingErrorType: ''
    });

    // Direct upload modal state
    const [directUploadModal, setDirectUploadModal] = useState<{
        isOpen: boolean;
        status: 'uploading' | 'error';
        recordingId: string | null;
    }>({
        isOpen: false,
        status: 'uploading',
        recordingId: null
    });

    // Use ref to track current direct upload modal state to avoid closure issues
    // and prevent BufferedEventBus replay from re-opening closed modals
    const directUploadModalRef = useRef<{
        isOpen: boolean;
        recordingId: string | null;
    }>({ isOpen: false, recordingId: null });

    // Track completed direct uploads to ignore ALL events for recordings that already completed
    // This prevents BufferedEventBus replay from affecting completed uploads
    const completedDirectUploadsRef = useRef<Set<string>>(new Set());

    // Listen for recording saved success event
    useEffect(() => {
        const handler = (event: any) => {
            const { recordingId, isOffline, isAutoSaved, wasInterrupted } = event;

            // Skip if recording was interrupted - the interruption alert will handle it
            if (wasInterrupted) {
                return;
            }

            // Skip if we've already shown a modal for this recording (prevents BufferedEventBus replay)
            if (recordingState.hasShownSuccessModal(recordingId)) {
                return;
            }
            recordingState.markSuccessModalShown(recordingId);

            const messageData = recordingState.getSuccessMessage(isOffline);

            setSuccessModalData({
                ...messageData,
                trackingType: isAutoSaved ? 'auto-saved' : 'user-saved',
                trackingTitle: messageData.title,
                trackingMessage: messageData.message,
                trackingIsOffline: isOffline || false
            });
            setSuccessModalOpen(true);
            successModalOpenRef.current = true;
        };

        const unsubscribe = events.on('RECORDING_SAVED_SUCCESS', handler);
        return unsubscribe;
    }, [events, recordingState]);

    // Listen for processing complete event
    useEffect(() => {
        const handler = (data: any) => {
            // Skip if we've already shown a modal for this recording (prevents BufferedEventBus replay)
            if (recordingState.hasShownProcessingComplete(data.recordingId)) {
                return;
            }
            recordingState.markProcessingCompleteShown(data.recordingId);

            // Check if this is a short recording without a summary
            const isShortRecording = data.classification?.isShort && !data.classification?.hasSummary;

            if (isShortRecording && data.shortRecordingMessage) {
                // Show short recording modal instead
                const shortModalState = {
                    isOpen: true,
                    message: data.shortRecordingMessage,
                    trackingWordCount: data.classification.wordCount,
                    trackingDuration: 0 // Duration tracked in classification
                };

                // Check ref instead of state to avoid closure issues
                if (successModalOpenRef.current) {
                    setSuccessModalOpen(false);
                    successModalOpenRef.current = false;
                    setTimeout(() => {
                        setShortRecordingModal(shortModalState);
                    }, 350); // Ionic modal animation duration
                } else {
                    setShortRecordingModal(shortModalState);
                }
            } else {
                // Show regular processing complete modal
                const modalState = {
                    isOpen: true,
                    message: data.message,
                    noteId: data.noteId,
                    trackingType: data.message.type as 'milestone' | 'encouragement',
                    trackingTitle: data.message.title,
                    trackingMessage: data.message.message,
                    trackingRecordingCount: data.stats.successfulSummaries,
                    trackingMilestone: data.message.type === 'milestone' ? data.stats.successfulSummaries : null,
                    trackingTimePresent: data.message.stats?.timePresent || null,
                    trackingHandwritingSaved: data.message.stats?.handwritingSaved || null,
                    trackingDuration: data.msDuration || null
                };

                // Check ref instead of state to avoid closure issues
                if (successModalOpenRef.current) {
                    setSuccessModalOpen(false);
                    successModalOpenRef.current = false;
                    setTimeout(() => {
                        setProcessingCompleteModal(modalState);
                    }, 350); // Ionic modal animation duration
                } else {
                    setProcessingCompleteModal(modalState);
                }
            }
        };

        const unsubscribe = events.on('PROCESSING_COMPLETE', handler);
        return unsubscribe;
    }, [events, recordingState]);

    // Listen for recording error event
    useEffect(() => {
        const handler = (event: any) => {
            const { recordingId, errorType, title, message } = event;

            // Skip if we've already shown a modal for this recording (prevents BufferedEventBus replay)
            if (recordingState.hasShownErrorModal(recordingId)) {
                return;
            }
            recordingState.markErrorModalShown(recordingId);

            const actionLabel = errorType === 'upload-failed' ? 'Got it' :
                                errorType === 'indexeddb-connection-lost' ? 'Understood' : 'OK';

            setErrorModal({
                isOpen: true,
                title,
                message,
                actionLabel,
                trackingErrorType: errorType
            });
        };

        const unsubscribe = events.on(RECORDING_ERROR_EVENT, handler);
        return unsubscribe;
    }, [events, recordingState]);

    // Listen for direct upload start event
    useEffect(() => {
        const handler = (event?: { recordingId: string }) => {
            if (!event) {
                return;
            }
            // Skip if this recording already completed (prevents BufferedEventBus replay)
            if (completedDirectUploadsRef.current.has(event.recordingId)) {
                return;
            }
            // Skip if modal is already open for this recording
            if (directUploadModalRef.current.isOpen && directUploadModalRef.current.recordingId === event.recordingId) {
                return;
            }
            directUploadModalRef.current = { isOpen: true, recordingId: event.recordingId };
            setDirectUploadModal({
                isOpen: true,
                status: 'uploading',
                recordingId: event.recordingId
            });
        };
        return events.on(RECORDING_DIRECT_UPLOAD_START, handler);
    }, [events]);

    // Listen for direct upload complete event
    useEffect(() => {
        const handler = (event?: { recordingId: string; success: boolean }) => {
            if (!event) {
                return;
            }
            if (event.success) {
                // Mark this recording as completed FIRST to block any subsequent events
                completedDirectUploadsRef.current.add(event.recordingId);
                // Only close modal if it's currently showing this recording
                if (directUploadModalRef.current.recordingId === event.recordingId) {
                    directUploadModalRef.current = { isOpen: false, recordingId: null };
                    setDirectUploadModal({
                        isOpen: false,
                        status: 'uploading',
                        recordingId: null
                    });
                }
            }
        };
        return events.on(RECORDING_DIRECT_UPLOAD_COMPLETE, handler);
    }, [events]);

    // Listen for direct upload error event
    useEffect(() => {
        const handler = (event?: { recordingId: string }) => {
            if (!event) {
                return;
            }
            // Skip if this recording already completed (prevents BufferedEventBus replay)
            if (completedDirectUploadsRef.current.has(event.recordingId)) {
                return;
            }
            // Only update if modal is open for this recording
            if (directUploadModalRef.current.isOpen && directUploadModalRef.current.recordingId === event.recordingId) {
                setDirectUploadModal(prev => ({
                    ...prev,
                    status: 'error'
                }));
            }
        };
        return events.on(RECORDING_DIRECT_UPLOAD_ERROR, handler);
    }, [events]);

    // Listen for direct upload retry event
    useEffect(() => {
        const handler = (event?: { recordingId: string }) => {
            if (!event) {
                return;
            }
            // Skip if this recording already completed (prevents BufferedEventBus replay)
            if (completedDirectUploadsRef.current.has(event.recordingId)) {
                return;
            }
            // Only update if modal is open for this recording
            if (directUploadModalRef.current.isOpen && directUploadModalRef.current.recordingId === event.recordingId) {
                setDirectUploadModal(prev => ({
                    ...prev,
                    status: 'uploading'
                }));
            }
        };
        return events.on(RECORDING_DIRECT_UPLOAD_RETRY, handler);
    }, [events]);

    return (
        <>
            {/* Recording Success Modal */}
            <RecordingSuccessModal
                isOpen={successModalOpen}
                title={successModalData.title}
                message={successModalData.message}
                trackingType={successModalData.trackingType}
                trackingTitle={successModalData.trackingTitle}
                trackingMessage={successModalData.trackingMessage}
                trackingIsOffline={successModalData.trackingIsOffline}
                onDismiss={() => {
                    setSuccessModalOpen(false);
                    successModalOpenRef.current = false;
                }}
            />

            {/* Processing Complete Modal */}
            <ProcessingCompleteModal
                isOpen={processingCompleteModal.isOpen}
                message={processingCompleteModal.message}
                trackingType={processingCompleteModal.trackingType}
                trackingTitle={processingCompleteModal.trackingTitle}
                trackingMessage={processingCompleteModal.trackingMessage}
                trackingRecordingCount={processingCompleteModal.trackingRecordingCount}
                trackingMilestone={processingCompleteModal.trackingMilestone}
                trackingTimePresent={processingCompleteModal.trackingTimePresent}
                trackingHandwritingSaved={processingCompleteModal.trackingHandwritingSaved}
                trackingDuration={processingCompleteModal.trackingDuration}
                onViewSummary={processingCompleteModal.noteId ? () => {
                    viewNoteDrawer.open({
                        noteId: processingCompleteModal.noteId!,
                        trackingFrom: 'Processing Complete Modal'
                    });
                    setProcessingCompleteModal(prev => ({ ...prev, isOpen: false }));
                } : undefined}
                onDismiss={() => setProcessingCompleteModal(prev => ({ ...prev, isOpen: false }))}
                onDidDismiss={() => setProcessingCompleteModal({
                    isOpen: false,
                    message: null,
                    noteId: null,
                    trackingType: 'encouragement',
                    trackingTitle: '',
                    trackingMessage: '',
                    trackingRecordingCount: 0,
                    trackingMilestone: null,
                    trackingTimePresent: null,
                    trackingHandwritingSaved: null,
                    trackingDuration: null
                })}
            />

            {/* Short Recording Modal */}
            <ShortRecordingModal
                isOpen={shortRecordingModal.isOpen}
                message={shortRecordingModal.message}
                trackingWordCount={shortRecordingModal.trackingWordCount}
                trackingDuration={shortRecordingModal.trackingDuration}
                onDismiss={() => setShortRecordingModal({
                    isOpen: false,
                    message: '',
                    trackingWordCount: 0,
                    trackingDuration: 0
                })}
            />

            {/* Recording Error Modal */}
            <RecordingErrorModal
                isOpen={errorModal.isOpen}
                title={errorModal.title}
                message={errorModal.message}
                actionLabel={errorModal.actionLabel}
                trackingErrorType={errorModal.trackingErrorType}
                onDismiss={() => setErrorModal(prev => ({ ...prev, isOpen: false }))}
                onDidDismiss={() => setErrorModal({
                    isOpen: false,
                    title: '',
                    message: '',
                    actionLabel: 'OK',
                    trackingErrorType: ''
                })}
            />

            {/* Recording Direct Upload Modal */}
            <RecordingDirectUploadModal
                isOpen={directUploadModal.isOpen}
                status={directUploadModal.status}
                recordingId={directUploadModal.recordingId}
                onRetry={() => {
                    if (directUploadModal.recordingId) {
                        recordingService.retryDirectUpload(directUploadModal.recordingId);
                    }
                }}
            />
        </>
    );
};
